export default {
  quickersim: "Quickersim",
  checkBestSolution: "Check which solution benefits you the most.",
  stepper: {
    step1: "Energy consumption profile",
    step2: "DSO and distribution tariff",
    step3: "Energy billing method",
    step4: "Connection and PV installation",
    step5: "User data",
    step6: "Results",
  },
  stepTitles: {
    step1: "Energy consumption profile",
    step2: "DSO and distribution tariff",
    step3: "Energy billing method",
    step4: "Connection and PV installation",
    step5: "User data",
    step6: "Analysis results",
  },
  clickHere: "Click here",
  learnMore: "to learn how we perform the calculations.",
  back: "Back",
  next: "Next",
  wantFreeAnalysis: "I want to receive a free analysis",
  step1: {
    q1: "How would you like to provide your energy consumption profile?",
    q1_PREDEFINED_PROFILE: "Select a predefined profile",
    q1_TABLE_INPUT: "Fill in a table with average daily consumption",
    q1_FILE_INPUT: "Upload an Excel file",
    profile_subForm: {
      q1: "Select one of the predefined profiles",
      q1_ONE_SHIFT: "Single-shift work with constant power consumption",
      q1_TWO_SHIFTS: "Double-shift work with constant power consumption",
      q1_THREE_SHIFTS: "Triple-shift work with constant power consumption",
      q2: "Enter monthly or annual consumption",
      q2_MONTHLY: "Monthly consumption",
      q2_YEARLY: "Annual consumption",
    },
    table_subform: {
      q1: "Fill in the table with average daily consumption",
      hour: "Hour",
      consumption: "Power consumption",
    },
    file_subform: {
      q1: "Upload an Excel file with your electricity consumption profile (provide power values in MW).",
    },
    validation: {
      predefinedProfile: "Select a predefined profile",
      consumptionType: "Select the type of consumption",
      consumptionRequired: "Enter the consumption value",
      tableNoData: "Fill in the consumption table",
      fileRequired: "Upload a file with the consumption profile",
      tableValueRequired: "Enter the consumption value",
      invalidExtension: "Invalid file extension. Allowed: {{ allowed }}.",
      fileTooLarge: "The file is too large. Maximum size is 10MB.",
      invalidType: "Invalid file type. Allowed: {{ allowed }}.",
      invalidColumnCount: "The file must have exactly {{ required }} columns.",
      invalidRowCount: "The file must have exactly {{ required }} rows (1 header + 24 data rows).",
      invalidSecondColumnName: 'The second column header must be "P_demand".',
      invalidDateFormat:
        "Invalid date format in column 1. Expected format: d.M.yyyy\u00a0\u00a0HH:mm:ss (e.g. 1.10.2024\u00a0\u00a003:00:00).",
    },
  },
  step2: {
    q1: "Select your Distribution System Operator (DSO)",
    q2: "Select your tariff",
    q1_operator: "Operator",
    q2_tariff: "Tariff",
    validation: {
      tariff: "Select a tariff",
      operator: "Select an operator",
    },
  },
  step3: {
    or: "or",
    q1: "How do you settle your energy payments?",
    q1_dynamic: "Dynamic rate",
    q1_constant: "Fixed price",
    q1_tariff: "Main supplier tariffs",
    q1_margin: "Margin",
    q1_fixedFee: "Monthly fixed fee",
    hintMarginAndFixedFee:
      "The monthly fixed fee is added to the whole month and does not affect the margin. The margin (PLN/MWh) is charged for each purchased unit of energy.",
    q1_price: "Price",
    q1_tariffLabel: "Tariff",
    validation: {
      billingType: "Select your energy billing method",
      marginOrFixedFeeRequired: "Enter either a margin or a monthly fixed fee",
      fixedPriceRequired: "Enter the price",
      tariffRequired: "Select a tariff",
    },
  },
  step4: {
    q1: "What are your connection conditions? (optional)",
    q2: "Is it possible to sell energy to the grid?",
    q3: "Do you have a PV installation?",
    importPower: "Import (optional)",
    exportPower: "Export (optional)",
    connectionPowerOverrideHintImportant: "Important:",
    connectionPowerOverrideHint:
      "If the entered connection capacity is lower than the maximum demand resulting from your profile data, the system will automatically increase it to the highest recorded demand value. We do this to ensure the technical correctness of the energy storage operation simulation.",
    exportPowerHint: "If selling energy to the grid is possible, fill in the export capacity.",
    installationPower: "DC-side installation capacity (panel power) [MWp]",
    inverterPower: "AC-side installation capacity (inverter power) [MW]",
    voivodeship: "Location (province)",
    validation: {
      canSellEnergy: "Select whether it is possible to sell energy",
      hasPvInstallation: "Select whether you have a PV installation",
      voivodeship: "Select a province",
      installationPowerRequired: "Enter PV installation capacity",
      exportPowerRecommended: "Provide export capacity",
    },
  },
  step5: {
    q1: "Fill in your details",
    q1_companyName: "Company name",
    q1_email: "E-mail",
    q1_firstName: "First name",
    q1_lastName: "Last name",
    q1_phoneNumber: "Phone number (optional)",
    q2: "Consents:",
    q2_selectAll: "Select all",
    q2_privacyPolicyRodo: "I have read the Privacy Policy and RODO information clause",
    q2_dataAccuracyResponsibilityConsent:
      "I confirm the accuracy of the entered data. I acknowledge that the calculator processes data automatically, and QuickerSim is responsible solely for the correctness of calculation algorithms based on the information provided.",
    q2_marketingEmailConsent:
      "I agree to receive commercial information electronically at the provided e-mail address from {{companyName}}.",
    q2_marketingPhoneConsent:
      "I agree to be contacted by phone for marketing purposes by {{companyName}}.",
    q2_newsletterConsent: "I want to receive the {{companyName}} newsletter.",
    q2_dataProcessingConsent:
      "I agree to the processing of my data in order to prepare and present an energy storage calculation.",
    agreementNote: "* Required consent.",
    validation: {
      companyName: "Please enter your company name",
      firstName: "Please enter your first name",
      lastName: "Please enter your last name",
      email: "Please enter a valid e-mail address",
      phoneNumber: "Please enter a valid phone number",
      privacyPolicyRodoRequired: "This consent is required",
      dataAccuracyResponsibilityConsentRequired: "This consent is required",
      dataProcessingConsentRequired: "This consent is required",
      invalidEmail: "Please enter a valid e-mail address",
      invalidPhone: "Please enter a valid phone number",
    },
  },
  step6: {
    prepositions: {
      from_before_consonant: "with",
      from_before_vowel: "with",
    },
    estimatedSavings: "Estimated energy storage savings",
    bestSavings: "Highest savings!",
    savingsDistribution: "Distribution savings",
    feeSavings: "Power fee savings",
    savingsPurchase: "Energy purchase savings",
    savingsTotal: "Total savings",
    calculationErrorMessage:
      "The calculations could not be completed based on the provided data. Please fill out the form again, make sure all information is correct, and submit it once more.",
    loadingHint:
      "Calculations may take a few minutes. You can safely close this tab - you will receive a link to the results by email.",
    driverLabels: {
      optimal: "QuickerSim",
      autoconsumption: "simple control maximizing self-consumption",
      autoconsumptionMinimizingPowerFee: "simple control minimizing capacity fee",
      autoconsumptionMaximizingAutoconsumption: "simple control maximizing self-consumption",
      fixed_hours: "simple control using a fixed schedule",
    },
  },
  consumption: "Consumption",
  powerConsumption: "Power consumption",
  MWh: "MWh",
  MWhValue: "{{value}} MWh",
  MW: "MW",
  MWValue: "{{value}} MW",
  zl: "PLN",
  zlValue: "{{value}} PLN",
  hour: "Hour",
  howDoWePerformCalculations: "How do we perform the calculations?",
  iWantToReceiveFreeAnalysis: "I want to receive a free analysis",
  refresh: "Refresh",
  failedToLoadData: "An error occurred while loading data.",
  noOptionsAvailable: "No options available.",
  yes: "Yes",
  no: "No",
  fullAnalysisCardTitle: "Want to know which energy storage system is best for you?",
  fullAnalysisCardSubtitle: "Request a detailed analysis from our specialist.",
  orderFullAnalysisButton: "Request full analysis",
  resultsCard:
    "The analysis results will be displayed below. This may take a few minutes. A link to the results will also be sent to the provided email address.",
  loading: "Loading...",
  fetchErrorMessage: "An error occurred while fetching the data.\nPlease try refresh.",
  summaryTitle:
    "Our specialist will conduct a detailed analysis to help you find the optimal energy storage solution for your business.",
  summarySubtitle: "What will you discover from the analysis?",
  summaryPoints: {
    point1: "How much you can save on your energy bills with a battery storage system.",
    point2: "Whether investing in energy storage is profitable for your company.",
    point3: "Comparison of different battery operation strategies.",
    point4: "Which energy storage system is ideal for your business.",
  },
  summary: {
    q1: "Check the accuracy of the information you entered earlier.",
    card1: "1. Your details",
    card2: "2. Form data",
    card2Description:
      "Verify the correctness of the data you entered earlier. Edit by clicking the pencil icon.",
    card3: "3. 1-year consumption profile",
    card3Description:
      "Upload an Excel file with your annual electricity consumption profile – you can obtain it from your Distribution System Operator.",
    card4: "4. Order summary",
    card4Description: "Summary",
    canSellEnergy: "Ability to sell energy",
    pvInstallation: "PV installation",
    exportPower: "Export",
    importPower: "Import",
    installationPower: "DC-side installation capacity (panel power) [MWp]",
    inverterPower: "AC-side installation capacity (inverter power) [MW]",
    billingMethod: "Method",
    price: "Price",
    card4Info: "The results will be sent to the email address provided above.",
    predefinedProfile: "Predefined profile",
    excelFile: "Excel file",
    table: "Average daily consumption table",
    monthlyConsumption: "Monthly consumption",
    yearlyConsumption: "Yearly consumption",
    consumptionType: "Type of consumption",
    file: {
      label:
        "Upload an Excel file with a 1-year electricity consumption profile (provide power values in MW) - you can obtain it from your Distribution System Operator.",
      exampleNote: "Download and view a sample file",
      unitsHint: "Important: in the Excel file, profile power values should be provided in MW.",
      exampleFileName: "example.xlsx",
      exampleDownloadToast: "Example file downloaded.",
      validation: {
        required: "Upload an Excel file with a 1-year electricity consumption profile.",
        invalidExtension: "Invalid file extension. Allowed: {{ allowed }}.",
        fileTooLarge: "The file is too large. Maximum size is 10MB.",
        invalidType: "Invalid file type. Allowed: {{ allowed }}.",
        invalidColumnCount: "The file must have exactly {{ required }} columns.",
        invalidRowCount:
          "The file must have exactly {{ required }} rows (1 header + 8760 hourly data rows).",
        invalidSecondColumnName: 'The second column header must be "P_demand".',
      },
    },
  },
  payAndOrderFullAnalysis: "Pay and order analysis",
  price: "Price",
  vat: "VAT 23%",
  totalPrice: "Total",
  done: "Done",
  mbOfMbMax: "{{mb}} MB of {{mb_max}} MB",
  powerDemand: "Maximum power demand: {{value}} MW",
  dropOr: "Drag and drop or",
  selectFile: "choose a file.",
  cancel: "Cancel",
  save: "Save",
  selectingPredefinedProfile: "selecting a predefined profile",
  voivodeships: {
    LOWER_SILESIAN: "Lower Silesia",
    KUYAVIAN_POMERANIAN: "Kuyavia-Pomerania",
    LUBLIN: "Lublin",
    LUBUSZ: "Lubusz",
    LODZ: "Łódź",
    LESSER_POLAND: "Lesser Poland",
    MASOVIAN: "Masovia",
    OPOLE: "Opole",
    SUBCARPATHIAN: "Subcarpathia",
    PODLASKIE: "Podlachia",
    POMERANIAN: "Pomerania",
    SILESIAN: "Silesia",
    HOLY_CROSS: "Holy Cross",
    WARMIAN_MASURIAN: "Warmia-Masuria",
    GREATER_POLAND: "Greater Poland",
    WEST_POMERANIAN: "West Pomerania",
  },
  formError: "An error occurred while submitting the form. Please try again.",
  checkoutSessionError:
    "We couldn't start the payment process. Please try again in a moment. If the problem persists, please contact us.",
  checkoutAlreadyPaidError:
    "This calculation has already been paid for. Check the results in your email or refresh the page to start a new calculation.",
  paymentSuccessTitle: "Payment successful!",
  thanksForUsingCalculator: "Thank you for using our calculator!",
  paymentSuccessDescription:
    "Your payment was successful and your order has been submitted for processing. Our specialist will prepare a detailed analysis showing which energy storage solution is optimal for your business. The results will be sent to the email address you provided.",
  processingTimeInfo:
    "Order processing may take up to few days. If you have any questions or issues, feel free to contact us!",
  thanksForTrust: "Thank you for your trust!",
  paymentFailureTitle: "Payment failed.",
  paymentFailureDescription:
    "The payment was not completed and your order has not been processed. To proceed with the analysis, please try completing the payment again.",
  retryPayment: "Try paying again",
  checkoutOpenedTitle: "Payment has been opened in a new tab",
  checkoutOpenedDescription:
    "Continue the payment in the newly opened tab. You will see the confirmation there after completion.",
  openPaymentAgain: "Open payment again",
  contactInfo: "If you have any questions, feel free to contact us!",
  fileDownloaded: "File downloaded.",
  errorDownloadingFile: "An error occurred while downloading the file. Please try again.",
  excelProfileUploadError: "An error occurred while uploading the Excel file. Please try again.",
  howDoWePerformCalculationsDescription: `To identify the most cost-effective energy storage system for your home or business, the calculator performs a series of intelligent calculations based on your data and real market conditions:

#### 1. We analyze your energy consumption profile
We collect from you data related to:
- historical energy consumption,
- your distribution system operator (DSO),
- your distribution tariff,
- connection capacity,
- your PV installation.

Additionally, we take into account energy prices from the last 12 months.  
If the provided consumption profile covers a period other than the last year, we adjust it to accurately reflect conditions from the last 12 months.

#### 2. We reconstruct the production of your PV system
We use historical weather data to accurately recreate how much energy your system could have generated in each hour of the year.

#### 3. We create a set of possible energy storage options
Based on your consumption profile and connection capacity, we select a list of storage capacities and power ratings that make sense for your situation.

#### 4. We simulate the operation of each storage system
An advanced algorithm determines the best charging and discharging strategy. We take into account:
- energy price variability,
- capacity charges and distribution costs,
- PV generation,
- the ability to profit from price differences (arbitrage), where regulations allow it.

#### 5. We choose the most financially attractive solution
We compare the savings delivered by each storage system with its estimated cost and recommend the option that performs best.

*Note: Storage system costs are based on average market prices and may differ from offers from specific suppliers. Before making an investment decision, it’s worth verifying them.*

Responsibility split: Our calculator is based on advanced, verified QuickerSim algorithms. We guarantee the mathematical and logical correctness of the simulation for the entered parameters. Keep in mind, however, that the final result is only as good as the data you provide. This analysis is for informational purposes and does not replace an official technical-economic project; we are not responsible for investment decisions made on its basis.`,
};
